/**
 * Rows for the participants editor, and the edit it sends.
 *
 * A row is one person who spoke on the call (a transcript carries their
 * participantId), with the roster's name and role when the roster has them.
 * Legacy sessions without participant ids have no rows, and the editor hides.
 * Ids are compared as strings: bot-mode (recall) ids are stored as numbers.
 */
import { getSpeakerLabel, isOwnMicParticipant, Participant, ParticipantRole, SpeakerLike } from '../../config/participants';

export interface ParticipantRow {
  participantId: string;
  name: string;
  role: ParticipantRole;
  turnCount: number;
  confirmed: boolean;
  /** A rep who joined with their own mic (`source: standalone_join`): role locked, never merged away. */
  hasOwnMic: boolean;
  /** The participant's first non-empty transcript line (untruncated), shown under the name. */
  firstLine?: string;
}

export interface ParticipantDraft {
  name: string;
  role: ParticipantRole;
}

export interface ParticipantEdit {
  participantId: string;
  name?: string;
  role?: ParticipantRole;
  /** Same person as this participant: the server moves every line here and drops this row. */
  mergeInto?: string;
  /** Split an automatic merge from this (former) participant back out (see participantSplit.ts). */
  splitOut?: true;
}

/** "Same as…" choices: source participantId -> target participantId. */
export type MergeMap = Readonly<Record<string, string>>;

const NO_MERGES: MergeMap = {};

export const isMerged = (merges: MergeMap, participantId: string): boolean =>
  Object.prototype.hasOwnProperty.call(merges, participantId);

/**
 * First lines longer than this are cut and end with an ellipsis. Fixed in
 * characters (not pixels) so the admin portal, web app and desktop apps agree.
 */
export const MAX_QUOTE_LENGTH = 80;

/** Cut a first line to MAX_QUOTE_LENGTH: the first 79 characters, trailing spaces trimmed, then "…". */
export function truncateQuote(text: string): string {
  const characters = Array.from(text);
  if (characters.length <= MAX_QUOTE_LENGTH) return text;
  return `${characters.slice(0, MAX_QUOTE_LENGTH - 1).join('').trimEnd()}\u2026`;
}

type RosterRow = Participant & { confirmed?: boolean };
type TranscriptLine = SpeakerLike & { text?: string };

const isRole = (value: unknown): value is ParticipantRole =>
  value === ParticipantRole.SALESPERSON || value === ParticipantRole.CUSTOMER;

/** Everyone who spoke, salespeople first, then by how much they spoke. */
export function buildParticipantRows(
  transcripts: TranscriptLine[] | undefined,
  participants: RosterRow[] | undefined,
): ParticipantRow[] {
  const roster = new Map((participants ?? []).map((row) => [String(row?.participantId), row]));
  const rows = new Map<string, ParticipantRow>();
  (transcripts ?? []).forEach((line) => {
    const rawId = line?.participantId;
    if (rawId === undefined || rawId === null || rawId === '') return;
    const participantId = String(rawId);
    const text = typeof line?.text === 'string' ? line.text.trim() : '';
    const existing = rows.get(participantId);
    if (existing) {
      existing.turnCount += 1;
      if (!existing.firstLine && text) existing.firstLine = text;
      return;
    }
    const rosterRow = roster.get(participantId);
    const role = isRole(rosterRow?.role) ? rosterRow!.role : isRole(line?.speaker) ? line.speaker : ParticipantRole.CUSTOMER;
    rows.set(participantId, {
      participantId,
      name: getSpeakerLabel({ ...line, speaker: role }, participants, { salespersonLabel: 'Salesperson' }),
      role,
      turnCount: 1,
      confirmed: Boolean(rosterRow?.confirmed),
      hasOwnMic: isOwnMicParticipant(rosterRow),
      ...(text ? { firstLine: text } : {}),
    });
  });
  return Array.from(rows.values()).sort((a, b) => {
    if (a.role !== b.role) return a.role === ParticipantRole.SALESPERSON ? -1 : 1;
    return b.turnCount - a.turnCount;
  });
}

/**
 * Only what changed, per row: the backend rewrites nothing it is not asked to.
 * A merged row sends only `mergeInto`; its own name and role are ignored. An
 * own-mic row never sends a role (the backend refuses a change there).
 */
export function editsFrom(
  rows: ParticipantRow[],
  drafts: Record<string, ParticipantDraft>,
  merges: MergeMap = NO_MERGES,
): ParticipantEdit[] {
  return rows.flatMap((row) => {
    if (isMerged(merges, row.participantId)) return [{ participantId: row.participantId, mergeInto: merges[row.participantId] }];
    const draft = drafts[row.participantId];
    if (!draft) return [];
    const edit: ParticipantEdit = { participantId: row.participantId };
    const name = draft.name.trim();
    if (name && name !== row.name) edit.name = name;
    if (draft.role !== row.role && !row.hasOwnMic) edit.role = draft.role;
    return edit.name !== undefined || edit.role !== undefined ? [edit] : [];
  });
}

/** Whether the drafts leave at least one salesperson (the backend refuses otherwise). Merged rows no longer count. */
export function keepsASalesperson(
  rows: ParticipantRow[],
  drafts: Record<string, ParticipantDraft>,
  merges: MergeMap = NO_MERGES,
): boolean {
  return rows.some(
    (row) =>
      !isMerged(merges, row.participantId) &&
      (drafts[row.participantId]?.role ?? row.role) === ParticipantRole.SALESPERSON,
  );
}

/** The name a row shows right now: the typed name when non-empty, else the original. */
export function displayNameOf(row: ParticipantRow, drafts: Record<string, ParticipantDraft> | null): string {
  return (drafts?.[row.participantId]?.name ?? '').trim() || row.name;
}
